import { LoaderCircle, Search, UserRound, X } from 'lucide-react'
import React, { useContext, useEffect, useMemo, useState } from 'react'
import { toast } from 'react-toastify'
import { BASE_URL_API } from '../../References/URLs'
import ActiveStoreContext from '../../store/ActiveStoreContext'
import AuthContext from '../../store/auth-context'
import styles from './StaffPickerModal.module.css'

const parseActiveStore = (rawStore) => {
    try {
        return rawStore && rawStore.trim() !== '' ? JSON.parse(rawStore) : null
    } catch {
        return null
    }
}

const normalizeStaff = (payload) => {
    const candidates = [
        payload?.staff,
        payload?.staffReadiness,
        payload?.staffList,
        payload?.response,
    ]
    const array = candidates.find(Array.isArray) || []

    return array
        .map((item, index) => ({
            staffIdentifier: item?.staffIdentifier || item?.employeeIdentifier || item?.id || `staff-${index}`,
            staffName: item?.staffName || item?.name || item?.fullName || 'Unnamed Staff',
            staffCode: item?.staffCode || item?.employeeCode || '',
            designationLabel: item?.designationLabel || item?.designation || '',
        }))
        .filter((item) => item.staffIdentifier)
}

const StaffPickerModal = ({ isOpen, onClose, onSelect }) => {
    const authCtx = useContext(AuthContext)
    const asCtx = useContext(ActiveStoreContext)
    const activeStore = parseActiveStore(asCtx?.activeStore)

    const [isLoading, setIsLoading] = useState(false)
    const [staffList, setStaffList] = useState([])
    const [search, setSearch] = useState('')

    useEffect(() => {
        if (!isOpen) return

        const fetchStaff = async () => {
            if (!authCtx?.token || !activeStore?.storeIdentifier) return

            try {
                setIsLoading(true)

                const response = await fetch(BASE_URL_API + 'getPayrollStaffReadiness', {
                    method: 'POST',
                    body: JSON.stringify({
                        token: authCtx.token,
                        storeIdentifier: activeStore.storeIdentifier,
                    }),
                })

                if (!response.ok) {
                    toast.error('Unable to load staff list.')
                    return
                }

                const respo = await response.json()

                if (respo?.status === 'success') {
                    setStaffList(normalizeStaff(respo?.response || {}))
                    return
                }

                if (respo?.message === 'tokenExpired') {
                    authCtx.logout()
                    return
                }

                toast.error(respo?.message || 'Unable to load staff list.')
            } catch {
                toast.error('Unable to load staff list.')
            } finally {
                setIsLoading(false)
            }
        }

        fetchStaff()
    }, [isOpen, authCtx, activeStore?.storeIdentifier])

    const filteredStaff = useMemo(() => {
        const query = search.trim().toLowerCase()
        if (!query) return staffList

        return staffList.filter((staff) => {
            const name = String(staff?.staffName || '').toLowerCase()
            const code = String(staff?.staffCode || '').toLowerCase()
            const identifier = String(staff?.staffIdentifier || '').toLowerCase()
            return name.includes(query) || code.includes(query) || identifier.includes(query)
        })
    }, [search, staffList])

    if (!isOpen) return null

    return (
        <div className={styles.overlay} role="dialog" aria-modal="true">
            <div className={styles.modal}>
                <div className={styles.header}>
                    <h3 className={styles.title}>Select Staff</h3>
                    <button type="button" className={styles.closeBtn} onClick={onClose}>
                        <X size={16} />
                    </button>
                </div>

                <div className={styles.searchWrap}>
                    <Search size={14} />
                    <input
                        type="text"
                        className={styles.searchInput}
                        placeholder="Search staff by name, code, or ID"
                        value={search}
                        onChange={(event) => setSearch(event.target.value)}
                    />
                </div>

                <div className={styles.listWrap}>
                    {isLoading ? (
                        <div className={styles.stateRow}>
                            <LoaderCircle size={16} className={styles.spinIcon} />
                            <span>Loading staff...</span>
                        </div>
                    ) : filteredStaff.length === 0 ? (
                        <div className={styles.stateRow}>No staff found.</div>
                    ) : (
                        filteredStaff.map((staff) => (
                            <button
                                key={staff.staffIdentifier}
                                type="button"
                                className={styles.staffRow}
                                onClick={() => onSelect?.(staff)}
                            >
                                <div className={styles.staffLeft}>
                                    <div className={styles.avatar}><UserRound size={14} /></div>
                                    <div>
                                        <p className={styles.staffName}>{staff.staffName}</p>
                                        <p className={styles.staffMeta}>
                                            {staff.designationLabel || 'Staff'}
                                            {staff.staffCode ? ` • ${staff.staffCode}` : ''}
                                        </p>
                                    </div>
                                </div>
                                <span className={styles.selectTxt}>Select</span>
                            </button>
                        ))
                    )}
                </div>
            </div>
        </div>
    )
}

export default StaffPickerModal
